import React, { useState, useEffect, useCallback, useRef } from 'react';
import get from 'lodash.get';
import { NavLink, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  isFeatureAvailable,
  isUserManager,
  hasPermission,
} from 'shared/helpers';
import { platformFeatures } from 'shared/constants';
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  IconDelete,
  IconEdit,
  Label,
  Modal,
  Notification,
  NotFound,
  Notice,
  Page,
  Selector,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
} from 'shared/components';
import OrderDetails from './components/OrderDetails';
import LicensesList from './components/LicensesList';
import OrderManagers from './components/OrderManagers';
import CustomerDetails from './components/CustomerDetails';
import { getOrderCore, patchOrder } from './actions';
import './styles.scss';

const OrderContainer = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const userDetails = useSelector((state) => get(state, 'user.details'));
  const customerAccounts = useSelector((state) =>
    get(state, 'account.customerAccounts', []),
  );
  const isManager = isUserManager(userDetails);
  const canManageManagers = hasPermission('manage_managers');
  const canManageCustomerAccounts = hasPermission('manage_customer_accounts');
  const hasAccountFeature = isFeatureAvailable(
    platformFeatures.extra_account_based_licensing,
  );

  const isMountedRef = useRef(false);

  const [isLoading, setLoading] = useState(true);
  const [isAccountUpdating, setAccountUpdating] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [order, setOrder] = useState(null);
  const [isAccountModalOpen, setAccountModalOpen] = useState(false);
  const [isRemoveAccountOpen, setRemoveAccountOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const fetchOrderCore = useCallback(() => {
    return getOrderCore(orderId).then((res) => get(res, 'data'));
  }, [orderId]);

  const refetchOrder = useCallback(() => {
    return fetchOrderCore().then((data) => {
      if (isMountedRef.current) {
        setOrder(data);
      }

      return data;
    });
  }, [fetchOrderCore]);

  useEffect(() => {
    isMountedRef.current = true;

    setLoading(true);
    setNotFound(false);

    refetchOrder()
      .then(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
        setNotFound(true);
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [refetchOrder]);

  const orderCustomerAccount = get(order, 'customer_account');
  const orderCustomerAccountId = get(orderCustomerAccount, 'id');
  const orderCustomerAccountLabel =
    get(orderCustomerAccount, 'name') ||
    get(orderCustomerAccount, 'code') ||
    get(orderCustomerAccount, 'id');
  const canManageOrderCustomerAccount = customerAccounts.some(
    (customerAccount) =>
      String(get(customerAccount, 'id')) === String(orderCustomerAccountId),
  );

  const openAccountModal = (accountId = null) => {
    setSelectedAccountId(accountId);
    setAccountModalOpen(true);
  };

  const closeAccountModal = () => setAccountModalOpen(false);

  const handleAccountSave = () => {
    if (!selectedAccountId || isAccountUpdating) {
      Notification('error', t('Select customer account'));
      return false;
    }
    setAccountUpdating(true);
    return patchOrder(orderId, { customer_account: selectedAccountId })
      .then(() => refetchOrder())
      .then(() => {
        setAccountModalOpen(false);
        Notification('success', t('Changes saved successfully'));
      })
      .catch(() => {
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      })
      .finally(() => setAccountUpdating(false));
  };

  const handleRemoveAccount = () => {
    if (isAccountUpdating) {
      return false;
    }
    setAccountUpdating(true);
    return patchOrder(orderId, { customer_account: null })
      .then(() => refetchOrder())
      .then(() => {
        setRemoveAccountOpen(false);
        Notification('success', t('Customer account removed from order'));
      })
      .catch(() => {
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      })
      .finally(() => setAccountUpdating(false));
  };

  if (!isManager || notFound) {
    return <NotFound />;
  }

  if (isLoading) {
    return (
      <Page>
        <ContentLoader text={t('Getting order details')} />
      </Page>
    );
  }

  return (
    <>
      {isRemoveAccountOpen && (
        <ConfirmationPopup
          closeCb={() => setRemoveAccountOpen(false)}
          confirmCb={handleRemoveAccount}
          title={t('Remove order from a customer account')}
          confirmText={t('Remove')}
          theme="error"
          warning
          disabled={isAccountUpdating}
        >
          <div className="OrderContainer-account-warning">
            <p>
              {t(
                'Are you sure you want to remove this order from the account?',
              )}
            </p>
            <Notice theme="warning" size="sm" title={t('Warning')}>
              {t(
                'Removing the customer account may make this order invisible to the license manager.',
              )}
            </Notice>
          </div>
        </ConfirmationPopup>
      )}
      {isAccountModalOpen && (
        <Modal
          closeCb={closeAccountModal}
          confirmCb={handleAccountSave}
          disabled={isAccountUpdating}
          title={
            orderCustomerAccountId
              ? t('Edit customer account')
              : t('Add to customer account')
          }
          size="md"
        >
          <div className="OrderContainer-account-modal">
            <Label
              text={t('Customer account')}
              inputId="order-customer-account"
            />
            <Selector
              id="order-customer-account"
              options={customerAccounts}
              value={selectedAccountId}
              handleChange={(val) => setSelectedAccountId(val)}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.id}
              labelKey="name"
              valueKey="id"
              placeholder={t('Select customer account')}
              isClearable
              isDisabled={!customerAccounts.length || isAccountUpdating}
            />
            {!customerAccounts.length && (
              <span className="OrderContainer-account-empty">
                {t('No customer accounts available')}
              </span>
            )}
          </div>
        </Modal>
      )}
      <Page
        title={`${t('Order')}: ${get(order, 'store_id')}`}
        breadcrumb={<NavLink to="/orders">{t('Orders')}</NavLink>}
        description={
          hasAccountFeature ? (
            <div className="OrderContainer-account-info">
              {orderCustomerAccountId ? (
                <div className="OrderContainer-account-wrapper">
                  {canManageOrderCustomerAccount ? (
                    <NavLink
                      to={`/customer-accounts/${orderCustomerAccountId}`}
                    >
                      {orderCustomerAccountLabel}
                    </NavLink>
                  ) : (
                    <span>{orderCustomerAccountLabel}</span>
                  )}
                  {canManageCustomerAccounts &&
                    canManageOrderCustomerAccount && (
                      <div className="OrderContainer-account-actions">
                        <button
                          type="button"
                          className="OrderContainer-account-button OrderContainer-account-button--edit"
                          onClick={() =>
                            openAccountModal(orderCustomerAccountId)
                          }
                          disabled={isAccountUpdating}
                        >
                          <IconEdit height="20px" width="20px" />
                        </button>
                        <button
                          type="button"
                          className="OrderContainer-account-button OrderContainer-account-button--delete"
                          onClick={() => setRemoveAccountOpen(true)}
                          disabled={isAccountUpdating}
                        >
                          <IconDelete
                            height="20px"
                            width="20px"
                            className="icon-button--delete"
                          />
                        </button>
                      </div>
                    )}
                </div>
              ) : canManageCustomerAccounts ? (
                <Button
                  key="customer-account"
                  onClick={() => openAccountModal(null)}
                  theme="default"
                  size="sm"
                >
                  {t('Add to Customer Account')}
                </Button>
              ) : (
                <span className="OrderContainer-account-empty">
                  {t('Not assigned')}
                </span>
              )}
            </div>
          ) : null
        }
      >
        <div className="OrderContainer-tabs">
          <Tabs>
            <TabsHeader>
              <Tab>{t('Order details')}</Tab>
              <Tab>{t('Licenses')}</Tab>
              {canManageManagers && <Tab>{t('License managers')}</Tab>}
              <Tab>{t('Customer')}</Tab>
            </TabsHeader>
            <TabContent>
              <OrderDetails order={order} />
            </TabContent>
            <TabContent>
              <LicensesList order={order} refetchOrder={refetchOrder} />
            </TabContent>
            {canManageManagers && (
              <TabContent>
                <OrderManagers
                  order={order}
                  customerEmail={get(order, 'customer.email')}
                />
              </TabContent>
            )}
            <TabContent>
              <CustomerDetails order={order} refetchOrder={refetchOrder} />
            </TabContent>
          </Tabs>
        </div>
      </Page>
    </>
  );
};

export default OrderContainer;
